<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { apiConfig, authStorage, request } from '../services/api'
import { refreshAccessToken } from '../services/auth'
import ChatFollowBetModal from './ChatFollowBetModal.vue'
import { useChatFollowBet } from '../services/chat-follow-bet'

const props = defineProps({ gameId: { type: [Number, String], required: true }, active: Boolean, profile: Object,
  bettingOpen: Boolean, gameNo: [String, Number], plays: { type: Array, default: () => [] } })
const emit = defineEmits(['unread', 'refresh'])
const messages = ref([])
const draft = ref('')
const connected = ref(false)
const online = ref(0)
const error = ref('')
const list = ref(null)
const filters = ref({ mine: true, others: true, collect: true, close_check: true, statistic: true })
const labels = { mine: '我的投注', others: '他人投注', collect: '开奖', close_check: '封盘列表', statistic: '报表' }
let socket, retryTimer, handshakeTimer, generation = 0, retries = 0, unread = 0, disposed = false, counter = 0
const seen = new Set()
const data = message => message?.data || {}
// Legacy liuhe bundle 28: chat -> envelope.member; bet -> data; bet_batch -> data.member.
const member = message => message.type === 'bet_batch' ? (data(message).member || {}) : message.type === 'bet' ? data(message) : (message.member || {})
const name = message => member(message).member_nickname || member(message).nickname || member(message).member_username || member(message).username || '会员'
function mine(message) {
  const ownId = props.profile?.member_id ?? props.profile?.id
  const senderId = member(message).member_id ?? member(message).id
  if (ownId != null && senderId != null) return String(ownId) === String(senderId)
  const ownName = props.profile?.member_username || props.profile?.username
  return Boolean(ownName && ownName === (member(message).member_username || member(message).username))
}
const bets = message => Array.isArray(data(message).lists) ? data(message).lists : [data(message)]
const period = message => data(message).game_no || data(message).game?.game_no || '—'
const cancelTarget = ref(null)
const cancelBusy = ref(false)
const cancelError = ref('')
function canCancel(message) {
  return props.bettingOpen && mine(message) && String(period(message)) === String(props.gameNo)
    && !data(message).cancel && bets(message).some(bet => bet.bet_id && !bet.cancel)
}
function markCanceled(id) {
  for (const message of messages.value) {
    if (!['bet', 'bet_batch'].includes(message.type)) continue
    for (const bet of bets(message)) if (String(bet.bet_id) === String(id)) bet.cancel = true
    if (bets(message).every(bet => bet.cancel)) message.data.cancel = true
  }
}
function requestCancel(message) {
  if (!canCancel(message) || cancelBusy.value) return
  cancelError.value = ''; cancelTarget.value = message
}
async function confirmCancel() {
  const message = cancelTarget.value
  if (!message || cancelBusy.value) return
  cancelError.value = ''
  if (!canCancel(message)) { cancelError.value = '已封盘、期号已变更或注单已撤销'; return }
  cancelBusy.value = true
  try {
    for (const bet of bets(message)) {
      if (bet.cancel) continue
      if (!canCancel(message)) throw new Error('已封盘或期号已变更，剩余注单未撤销')
      if (!bet.bet_id) throw new Error('注单编号缺失，请刷新后重试')
      await request('/game/bet/cancel', { method: 'POST', data: { bet_id: bet.bet_id } })
      markCanceled(bet.bet_id)
    }
    cancelTarget.value = null
  } catch (failure) { cancelError.value = failure.message || '撤单结果未确认，请刷新投注记录核对' }
  finally { cancelBusy.value = false; emit('refresh') }
}
const balance = computed(() => Number(props.profile?.balance ?? props.profile?.money ?? 0))
function followPlay(bet) {
  return props.plays.find(play => bet.play_id != null ? String(play.play_id) === String(bet.play_id) : play.play_name === bet.play_name)
}
function canFollow(message) {
  return props.bettingOpen && !mine(message) && !data(message).cancel
    && String(period(message)) === String(props.gameNo) && bets(message).length > 0
    && bets(message).every(bet => !bet.cancel && followPlay(bet))
}
const { target: followTarget, busy: followBusy, error: followError, toast: followToast,
  validation: followValidation, open: openFollow, close: closeFollow, confirm: confirmFollow } = useChatFollowBet({
  balance,
  allowed: target => String(target.game) === String(props.gameId) && canFollow(target.message),
  send: async (entries, target) => {
    const rows = entries.map(entry => {
      const play = props.plays.find(row => String(row.play_id) === String(entry.playId))
      const money = Number(entry.amount)
      if (!play || money < Number(play.bet_min || 0) || (Number(play.bet_max_once) > 0 && money > Number(play.bet_max_once))) {
        throw new Error('跟投金额不符合当前玩法限额，请调整金额')
      }
      return { play_id: play.play_id, bet_money: String(entry.amount), bet_content: play.bet_content }
    })
    const response = await request('/game/bet/bet_batch', { method: 'POST', data: {
      game: target.game, game_no: target.period, bet_lists: rows,
    } })
    acceptBetReceipt(response.data)
  },
  afterSuccess: () => emit('refresh'),
})
function follow(message) {
  if (!canFollow(message)) return
  openFollow({ message, game: props.gameId, period: period(message), player: name(message),
    entries: bets(message).map(bet => ({ playId: followPlay(bet).play_id,
      label: followPlay(bet).play_title, amount: bet.bet_money })),
  })
}
const amount = value => Number(value || 0).toFixed(2)
// Same potential-profit calculation as FantanBetPage, using the bet's odds snapshot.
function profit(bet) {
  const supplied = bet.bet_pl ?? bet.pl_money ?? bet.win_money
  if (supplied != null) return amount(supplied)
  const odds = Number(bet.bet_odds ?? bet.odds)
  return Number.isFinite(odds) && odds > 0 ? amount(Number(bet.bet_money || 0) * (odds - 1)) : '—'
}
const statisticName = row => row.member?.member_nickname || row.member?.member_username || row.member_nickname || '会员'
const reportPage = message => data(message).page?.total ? `（${data(message).page.index || 1}/${data(message).page.total}）` : ''
const defaultAvatar = '/legacy/images/userface-default.png'
function avatar(message) {
  const value = member(message).member_avatar || member(message).avatar
    || (mine(message) ? props.profile?.avatar || props.profile?.member_avatar : '')
  // Match the existing chat's legacy default-avatar mapping and error fallback.
  if (!value || value === '/assets/userface.png') return defaultAvatar
  try {
    const base = new URL(apiConfig.apiServer, import.meta.env.DEV ? (import.meta.env.VITE_DEV_API_SERVER || 'https://api3.137n.com') : location.origin)
    const url = new URL(value, base.origin + '/')
    if (url.hostname === base.hostname && base.protocol === 'https:' && url.protocol === 'http:') url.protocol = 'https:'
    return ['http:', 'https:'].includes(url.protocol) ? url.href : defaultAvatar
  } catch (_) { return defaultAvatar }
}
function onAvatarError(event) {
  const img = event.target
  if (img.getAttribute('src') !== defaultAvatar) img.src = defaultAvatar
}
function timeLabel(value) {
  const date = new Date(Number(value) * 1000)
  return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', hourCycle: 'h23', hour: '2-digit', minute: '2-digit' }).format(date) : ''
}
function announcementTime(value) {
  if (!Number(value)) return ''
  return new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', hourCycle: 'h23',
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(new Date(Number(value) * 1000))
}
function balls(message) {
  const raw = data(message).format_lottery_numbers?.source || data(message).lottery_numbers || ''
  const values = (Array.isArray(raw) ? raw : String(raw).split(',')).map(Number)
  return values.length === 7 && new Set(values).size === 7 && values.every(n => Number.isInteger(n) && n >= 1 && n <= 49) ? values : []
}
const red = new Set([1,2,7,8,12,13,18,19,23,24,29,30,34,35,40,45,46])
const blue = new Set([3,4,9,10,14,15,20,25,26,31,36,37,41,42,47,48])
const color = n => red.has(n) ? 'red' : blue.has(n) ? 'blue' : 'green'
function text(message) {
  // Legacy service HTML-encodes stored text. Decode to a string, never render as HTML.
  const el = document.createElement('textarea')
  el.innerHTML = String(data(message).message || data(message).content || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return el.value
}
const visible = computed(() => {
  let lastTime = 0
  return messages.value.filter(message => {
  if (message.type === 'bet' || message.type === 'bet_batch') return filters.value[mine(message) ? 'mine' : 'others']
  return filters.value[message.type] !== false
  }).map(message => {
    const timestamp = Number(message.time)
    const showTime = timestamp > 0 && timestamp - lastTime >= 60
    if (showTime) lastTime = timestamp
    return { ...message, showTime }
  })
})
async function scroll() { await nextTick(); if (props.active && list.value) list.value.scrollTop = list.value.scrollHeight }
function appendMessage(message) {
  // The HTTP receipt and the queue push contain the same committed bet IDs.
  // Deduplicate individual bets, including split/overlapping batch replays.
  if (['bet', 'bet_batch'].includes(message.type)) {
    const existingIds = new Set(messages.value.filter(row => ['bet', 'bet_batch'].includes(row.type))
      .flatMap(row => bets(row).map(bet => String(bet.bet_id))))
    const rows = bets(message).filter(bet => bet.bet_id != null && !existingIds.has(String(bet.bet_id)))
    if (!rows.length) return
    message = { ...message, data: message.type === 'bet_batch' ? { ...data(message), lists: rows } : rows[0] }
  }
  const key = data(message).message_id || message.id || JSON.stringify([message.type, message.time, message.member, message.data])
  if (seen.has(key)) return
  seen.add(key)
  if (seen.size > 300) seen.delete(seen.values().next().value)
  messages.value.push({ ...message, key: ++counter })
  if (messages.value.length > 100) messages.value.shift()
  if (!props.active) { unread++; emit('unread', unread) }
  void scroll()
}
function acceptBetReceipt(receipt) {
  // Never reconstruct a success message from selected numbers or entered amounts.
  if (String(receipt?.game?.game_id) !== String(props.gameId)
    || !receipt?.member || !Array.isArray(receipt.lists) || !receipt.lists.length
    || receipt.lists.some(bet => !bet.bet_id)) return
  appendMessage({ type: 'bet_batch', data: receipt })
}
defineExpose({ acceptBetReceipt })
watch(() => props.active, value => { if (value) { unread = 0; emit('unread', 0); void scroll() } })
function stop() {
  generation++
  clearTimeout(retryTimer); clearTimeout(handshakeTimer)
  const previous = socket; socket = null; connected.value = false
  previous?.close(3001, 'exit')
}
async function connect() {
  if (disposed || !props.gameId) return
  const version = ++generation
  error.value = ''
  let token = authStorage.getAccessToken()
  try { token = (await refreshAccessToken()) || token } catch (_) { /* Surface any auth failure from handshake. */ }
  if (disposed || version !== generation) return
  if (!token) { error.value = '登录已失效，请重新登录'; return }
  const ws = new WebSocket(apiConfig.wsServer)
  socket = ws
  handshakeTimer = setTimeout(() => { if (socket === ws && !connected.value) ws.close() }, 10000)
  ws.onopen = () => {
    if (version !== generation) return
    ws.send(JSON.stringify({ type: 'connect', game: props.gameId, client: 'web-user', token, reconnect: retries ? 1 : 0 }))
  }
  ws.onmessage = event => {
    if (version !== generation) return
    try {
      const response = JSON.parse(event.data)
      if (Number(response.error_code) !== 0) {
        error.value = response.error_msg === 'chat muted' ? '当前账号已被禁言' : response.error_msg || '聊天室请求被拒绝'
        if ([1001, 501].includes(Number(response.error_code))) { stop(); retries = 10 }
        return
      }
      const message = response.data
      if (!message?.type) return
      if (['connect', 'online', 'close'].includes(message.type)) {
        online.value = Number(data(message).count || 0)
        if (message.type === 'connect') { clearTimeout(handshakeTimer); connected.value = true; retries = 0 }
        return
      }
      if (['recharge_handle', 'cash_handle', 'cash_cancel'].includes(message.type)) { emit('refresh'); return }
      if (message.type === 'bet_cancel') {
        markCanceled(data(message).bet_id)
        emit('refresh'); return
      }
      if (!['chat', 'bet', 'bet_batch', 'collect', 'close_check', 'statistic'].includes(message.type)) return
      appendMessage(message)
      if (message.type === 'collect' || message.type === 'statistic') emit('refresh')
    } catch (_) { /* A malformed packet must not interrupt later messages. */ }
  }
  ws.onclose = event => {
    if (version !== generation || disposed) return
    clearTimeout(handshakeTimer); connected.value = false; socket = null
    if (event.code === 3001 || retries >= 10) { error.value = '聊天室连接已断开，请重新进入游戏'; return }
    retries++
    retryTimer = setTimeout(connect, Math.min(1000 * retries, 10000))
  }
  ws.onerror = () => { if (version === generation) error.value = '聊天室暂时无法连接' }
}
function send() {
  const message = draft.value.trim()
  if (!message || !connected.value || socket?.readyState !== WebSocket.OPEN) return
  try { socket.send(JSON.stringify({ type: 'chat', game: props.gameId, message })); draft.value = ''; error.value = '' }
  catch (_) { error.value = '发送失败，请重试' }
}
watch(() => props.gameId, () => { stop(); messages.value = []; seen.clear(); unread = 0; retries = 0; emit('unread', 0); void connect() }, { immediate: true })
onBeforeUnmount(() => { disposed = true; stop() })
</script>

<template>
  <section class="liuhe-chat">
    <div v-if="error" class="connection" role="alert">{{ error }}</div>
    <div ref="list" class="chat-messages">
      <article v-for="message in visible" :key="message.key" class="msg-item">
        <div v-if="message.showTime" class="msg-time">{{ timeLabel(message.time) }}</div>
        <div :class="['message', { mine: mine(message) }]">
        <template v-if="['chat', 'bet', 'bet_batch'].includes(message.type)">
          <img class="avatar" :src="avatar(message)" alt="" @error="onAvatarError">
          <div class="message-content"><small>{{ name(message) }}</small>
            <p v-if="message.type === 'chat'" class="bubble">{{ text(message) }}</p>
            <div v-else class="bubble bet-message"><div class="bet-count">{{ mine(message) ? '我刚刚投了' : '刚刚投了' }} <b>{{ period(message) }}</b> 期 {{ bets(message).length }} 注</div><div class="bet-detail"><div><p v-for="(bet, i) in bets(message)" :key="i"><b>{{ bet.play_title || bet.play_name || bet.bet_content }}</b> 投了：<b>{{ amount(bet.bet_money) }}</b>，可赢：<b>{{ profit(bet) }}</b><span v-if="bet.cancel">（已取消）</span></p></div><button :disabled="cancelBusy || followBusy || (mine(message) ? !canCancel(message) : !canFollow(message))" @click="mine(message) ? requestCancel(message) : follow(message)">{{ data(message).cancel ? '已取消' : mine(message) ? '取消' : '跟投' }}</button></div></div>
          </div>
        </template>
        <div v-else-if="message.type === 'collect'" class="system-message"><strong>第 {{ period(message) }} 期开奖公告</strong><p v-if="data(message).draw_at">{{ announcementTime(data(message).draw_at) }}</p><div class="chat-balls"><template v-for="(n, i) in balls(message)" :key="i"><span v-if="i === 6">＋</span><i :class="color(n)">{{ String(n).padStart(2, '0') }}</i></template></div></div>
        <div v-else-if="message.type === 'close_check'" class="system-message announcement"><strong>第 {{ period(message) }} 期封盘核对表{{ reportPage(message) }}</strong><p>{{ announcementTime(data(message).closed_at) }}</p><p>本期有效投注总额：{{ amount(data(message).total_money) }}</p><template v-if="Array.isArray(data(message).rows)"><div class="close-row"><span>昵称</span><span>玩法</span><span>金额</span></div><div v-for="row in data(message).rows" :key="row.bet_id" class="close-row"><span>{{ row.member_name }}</span><span>{{ row.play_title }}</span><span>{{ amount(row.bet_money) }}</span></div><p v-if="!data(message).rows.length">本期暂无有效投注</p></template><template v-else><div v-for="gate in data(message).gates || []" :key="gate.gate"><b>【{{ gate.gate }}门总投：{{ amount(gate.total_money) }}】</b><p v-for="row in gate.rows || []" :key="row.bet_id">{{ row.member_name }} - 投：{{ amount(row.bet_money) }}</p></div></template></div>
        <div v-else class="system-message"><strong>第 {{ period(message) }} 期开奖报表{{ reportPage(message) }}</strong><div class="report-row"><span>昵称</span><span>下注</span><span>中奖</span><span>盈利</span></div><div v-for="(row, i) in data(message).lists || []" :key="i" class="report-row"><span>{{ statisticName(row) }}</span><span>{{ amount(row.bet_money) }}</span><span>{{ amount(row.bet_money_win) }}</span><span :class="{ loss: Number(row.bet_money_pl) < 0 }">{{ amount(row.bet_money_pl) }}</span></div></div>
        </div>
      </article>
    </div>
    <footer><div class="filters"><button v-for="(label, key) in labels" :key="key" type="button" :class="{ checked: filters[key] }" :aria-pressed="filters[key]" @click="filters[key] = !filters[key]">{{ label }}</button></div><form @submit.prevent="send"><input v-model="draft" aria-label="聊天内容" placeholder="请输入您要发送的内容" maxlength="1000"><button :disabled="!connected || !draft.trim()">发送</button></form></footer>
    <ChatFollowBetModal :target="followTarget" :balance="balance" :busy="followBusy" :error="followError" :validation="followValidation" :toast="followToast" @close="closeFollow" @confirm="confirmFollow" />
    <Teleport to="body">
      <div v-if="cancelTarget" class="legacy-cancel-mask">
        <section class="legacy-cancel-dialog" role="dialog" aria-modal="true" aria-label="确认取消" :aria-busy="cancelBusy">
          <h2>请确认</h2><p>您确认取消吗？</p>
          <p v-if="cancelError" role="alert">{{ cancelError }}</p>
          <footer><button :disabled="cancelBusy" @click="cancelTarget = null">关闭</button><button :disabled="cancelBusy || !canCancel(cancelTarget)" @click="confirmCancel">{{ cancelBusy ? '撤单中…' : '确认' }}</button></footer>
        </section>
      </div>
    </Teleport>
  </section>
</template>

<style scoped>
/* Geometry/colors transcribed from legacy web/28.4dd884482bedd058a2bd.js chat CSS.
   The fifth filter follows the supplied screenshot and the existing close_check contract. */
.liuhe-chat{display:flex;flex:1;min-height:0;flex-direction:column;background:#f5f5f5;font-size:12.8px}
.connection{padding:5px 15px;font-size:12px;color:#ba3535}
.chat-messages{min-height:0;flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:0 15px}
.msg-item{margin:8px 0}.msg-time{text-align:center;color:#ccc;font-size:11.2px}
.message{display:flex;align-items:flex-start;justify-content:flex-start}
.avatar{flex:0 0 32px;width:32px;height:32px;min-width:32px;object-fit:cover}
.message-content{position:relative;margin:0 8px;max-width:85%;min-width:0}
.message-content>small{display:block;color:#666;margin-bottom:1.6px;font-size:inherit}
.bubble{margin:0;padding:8px 16px;border-radius:3.2px;background:#fff;color:#000;line-height:1.5;position:relative;overflow-wrap:anywhere;white-space:pre-wrap}
.mine{flex-direction:row-reverse}.mine .message-content>small{text-align:right}
.mine .bubble{background:#9eea6a}
.bubble:not(.bet-message):before{content:'';position:absolute;left:-11.2px;top:3.2px;border-style:solid;border-width:6.4px;border-color:transparent #fff transparent transparent}
.mine .bubble:not(.bet-message):before{left:auto;right:-11.2px;border-color:transparent transparent transparent #9eea6a}
.bet-message:before{content:'';position:absolute;left:-11.2px;top:6.4px;border-style:solid;border-width:6.4px;border-color:transparent #fff transparent transparent}
.mine .bet-message:before{left:auto;right:-11.2px;border-color:transparent transparent transparent #9eea6a}
.bet-message{padding:0}.bet-count{padding:5px 15px}.bet-count b,.bet-detail b{color:red}
.bet-detail{padding:5px 15px;border-top:1px solid #e5e5e5;display:flex;align-items:center;font-size:11.2px;gap:16px}.bet-detail>div{flex:1}.bet-detail p{margin:0 0 3.2px}
.bet-detail button{border:0;border-radius:3.2px;background:#ccc;color:#fff;padding:4px 12px}
.bet-detail button:not(:disabled){background:#f04141;cursor:pointer}
.legacy-cancel-mask{position:fixed;inset:0;z-index:1200;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.32)}.legacy-cancel-dialog{width:250px;background:#fff;color:#222}.legacy-cancel-dialog h2{margin:0;padding:20px 24px 12px;font-size:20px;font-weight:500}.legacy-cancel-dialog p{margin:0;padding:0 24px 30px;color:#888;font-size:14px}.legacy-cancel-dialog footer{display:flex;justify-content:flex-end;gap:25px;padding:12px 24px 18px}.legacy-cancel-dialog button{border:0;background:transparent;padding:0;color:#f04141;font-size:14px}.legacy-cancel-dialog button:disabled{color:#aaa}
.system-message{width:100%;text-align:center;font-size:12.8px;padding:8px 0;border:1px solid #e5e5e5;border-radius:3.2px;background:#2fbc8b;color:#fff}
.close-row{display:grid;grid-template-columns:1.4fr 1fr 1fr;line-height:2;overflow-wrap:anywhere}.close-row span{min-width:0;padding:0 4px}.close-row span:last-child{color:#fff}
.system-message strong{font-weight:400}.system-message p{margin:5px 0}
.chat-balls{display:flex;justify-content:center;align-items:center;gap:5px;margin-top:9px}
.chat-balls i{display:grid;place-items:center;width:24px;height:24px;border-radius:50%;font-style:normal;color:#fff}
.red{background:#e8353d}.blue{background:#2f7fd6}.green{background:#1f9d55}
.report-row{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));line-height:2;overflow-wrap:anywhere}
.report-row span{min-width:0}.report-row span:not(:first-child){color:red}.report-row .loss{color:#ff0!important}
footer{background:transparent;min-height:32px}
.filters{display:flex;align-items:center;justify-content:space-around;padding:5px 0;background:#f5f5f5}
.filters button{display:inline-block;border:1px dashed #ccc;padding:2px 5px;color:#ccc;background:transparent;font-size:11.2px;line-height:normal;border-radius:0}
.filters button.checked{border-color:red;color:red}
form{display:flex;background:#fff;padding:15px}
form>input{min-width:0;flex:1;line-height:1;height:auto;padding:6.4px;margin:0;border:none;background:#f5f5f5;font-size:12.8px;border-radius:4.8px}
form>button{background:#f04141;color:#fff;margin-left:6.4px;line-height:24px;text-align:center;font-size:12.8px;padding:4.8px 16px;border:none;border-radius:3.2px}
form>button:disabled{background:#ccc}
</style>
